import { OptionsApi } from '@core/swagger-client';
import { WebinarStatus } from '@core/types';
import { ContainerDefault, ErrorMessagePage, LoaderPage, Warning } from '@core/ui';
import { ViewLiveWebinarOrganism, ViewOnDemandWebinarOrganism } from '@online-webinar/player';
import { Link, useParams } from '@online-webinar/router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export const ViewAnonymousPage = ({ countdownImageUrl, countdownVideoUrl }: { countdownImageUrl?: string; countdownVideoUrl?: string }) => {
    const { t } = useTranslation();
    const { webinarId, authorizationCode } = useParams({ strict: false }) as { webinarId: string; authorizationCode?: string };
    const { data: webinar, error, isLoading } = useQuery(OptionsApi.anonymous.getStatus(webinarId, authorizationCode));

    useEffect(() => {
        document.body.classList.add('no-menu');
        return () => {
            document.body.classList.remove('no-menu');
        };
    }, []);

    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isLoading || webinar === undefined) {
        return <LoaderPage />;
    }

    if (!webinar.canViewAnonymously) {
        return (
            <ContainerDefault>
                <Warning classNames="w-10 md:w-12 lg:w-16 stroke-warning mb-4" />
                <div className="font-accent text-3xl md:text-4xl lg:text-4xl mb-2 md:mb-6">{t('Anonymous.NotAvailable.Heading')}</div>
                <p>{t('Anonymous.NotAvailable.Body')}</p>
                <Link to="/$webinarId" params={{ webinarId }} className="btn btn-primary mt-6">
                    {t('Button.Back')}
                </Link>
            </ContainerDefault>
        );
    }

    let anonymousId = globalThis.sessionStorage.getItem('AnonymousId');
    if (!anonymousId) {
        anonymousId = crypto.randomUUID();
        globalThis.sessionStorage.setItem('AnonymousId', anonymousId);
    }

    if (webinar.status === WebinarStatus.Planned || webinar.status === WebinarStatus.Started) {
        return (
            <ViewLiveWebinarOrganism
                webinarId={webinarId!}
                anonymousId={anonymousId}
                anonymousAuthorizationCode={authorizationCode}
                countdownImageUrl={countdownImageUrl}
                countdownVideoUrl={countdownVideoUrl}
            />
        );
    }

    if (webinar.status === WebinarStatus.Ended) {
        return <ViewOnDemandWebinarOrganism webinarId={webinarId!} anonymousId={anonymousId} anonymousAuthorizationCode={authorizationCode} />;
    }

    return <ErrorMessagePage errorCode="404" />;
};
